import * as React from 'react'
import { Loader2, Plus } from 'lucide-react'
import {
  TREE_MARK_COLORS,
  TREE_MARK_COLOR_LABELS,
  TREE_MARK_SHAPES,
  TREE_MARK_SHAPE_LABELS,
  type TreeMarkColor,
  type TreeMarkShape,
} from '@lib/enums/tree-mark.enum'
import { createTreeSpeciesInputSchema } from '@lib/input/tree-species.input'
import type { TreeSpeciesDto } from '@lib/dto/tree-species.dto'
import {
  useArchiveTreeSpecies,
  useCreateTreeSpecies,
  useTreeSpecies,
  useUpdateTreeSpecies,
} from '@/hooks/useTreeSpecies'
import { errorMessage } from '@/helpers/logger'
import { TreeGlyph } from '@/field-map/TreeGlyph'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * The first free shape/color pair, for a new species.
 *
 * Offering a combination nobody is using beats defaulting to the top of each
 * list, which would hand every new species the same mark and put the burden of
 * noticing on the person adding it. Falls back to the first of each once the
 * palette is exhausted — a hundred-odd species in, where a duplicate mark is
 * the least of the legend's problems.
 */
function firstFreeMark(existing: TreeSpeciesDto[]): {
  shape: TreeMarkShape
  color: TreeMarkColor
} {
  const taken = new Set(
    existing.map((entry) => `${entry.shape}:${entry.color}`),
  )
  for (const color of TREE_MARK_COLORS) {
    for (const shape of TREE_MARK_SHAPES) {
      if (!taken.has(`${shape}:${color}`)) {
        return { shape, color }
      }
    }
  }
  return { shape: TREE_MARK_SHAPES[0], color: TREE_MARK_COLORS[0] }
}

/** The shape and color pickers, shared by the add form and each row's editor. */
function MarkPicker({
  shape,
  color,
  onChange,
}: {
  shape: TreeMarkShape
  color: TreeMarkColor
  onChange: (mark: { shape: TreeMarkShape; color: TreeMarkColor }) => void
}) {
  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <Label>Shape</Label>
        <div className="flex flex-wrap gap-1">
          {TREE_MARK_SHAPES.map((option) => (
            <button
              key={option}
              type="button"
              aria-label={TREE_MARK_SHAPE_LABELS[option]}
              aria-pressed={option === shape}
              title={TREE_MARK_SHAPE_LABELS[option]}
              onClick={() => {
                onChange({ shape: option, color })
              }}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                option === shape
                  ? 'border-brand bg-brand-subtle'
                  : 'border-default hover:bg-subtle',
              )}
            >
              {/* Drawn in the chosen color so the two pickers read as one
                  decision rather than two independent lists. */}
              <TreeGlyph species={{ name: '', shape: option, color }} size={18} />
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Color</Label>
        <div className="flex flex-wrap gap-1">
          {TREE_MARK_COLORS.map((option) => (
            <button
              key={option}
              type="button"
              aria-label={TREE_MARK_COLOR_LABELS[option]}
              aria-pressed={option === color}
              title={TREE_MARK_COLOR_LABELS[option]}
              onClick={() => {
                onChange({ shape, color: option })
              }}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                option === color
                  ? 'border-brand bg-brand-subtle'
                  : 'border-default hover:bg-subtle',
              )}
            >
              <TreeGlyph
                species={{ name: '', shape, color: option }}
                size={18}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Managing the species a survey can record.
 *
 * The marks are a closed palette rather than a color picker, and that is a
 * deliberate constraint rather than a shortcut: hue alone fails a color-blind
 * reader and fails again in the grayscale print these maps end up in, so shape
 * carries the species a second time. Twelve colors × nine shapes is a hundred
 * and eight distinguishable marks, which is more species than a tree-care
 * business in one region will ever survey.
 */
export function TreeSpeciesSettings() {
  const species = useTreeSpecies()
  const createSpecies = useCreateTreeSpecies()
  const updateSpecies = useUpdateTreeSpecies()
  const archiveSpecies = useArchiveTreeSpecies()

  const [isAdding, setIsAdding] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const rows = species.data ?? []

  if (species.isPending) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
        </div>
      </section>
    )
  }

  if (species.isError) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4 text-center">
        <p className="text-sm font-medium">Species could not be loaded</p>
        <p className="mt-1 text-sm text-muted-text">
          Check your connection and try again.
        </p>
      </section>
    )
  }

  async function handleArchiveToggle(entry: TreeSpeciesDto): Promise<void> {
    setError(null)
    try {
      await archiveSpecies.mutateAsync({
        id: entry.id,
        archived: entry.archivedAt === null,
      })
    } catch (caught) {
      setError(errorMessage(caught, 'Could not save. Please try again.'))
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Tree species</h2>
          <p className="mt-0.5 text-sm text-muted-text">
            What a surveyor can choose from, and the mark each one gets on a
            map. Shape as well as color, so a legend still works in grayscale
            print and for a color-blind reader.
          </p>
        </div>
        {isAdding ? null : (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setError(null)
              setEditingId(null)
              setIsAdding(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add species
          </Button>
        )}
      </div>

      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      {isAdding ? (
        <SpeciesForm
          initial={{ name: '', ...firstFreeMark(rows) }}
          isSaving={createSpecies.isPending}
          submitLabel="Add species"
          onCancel={() => setIsAdding(false)}
          onSubmit={async (draft) => {
            setError(null)
            const parsed = createTreeSpeciesInputSchema.safeParse(draft)
            if (!parsed.success) {
              setError(parsed.error.issues[0]?.message ?? 'Check the details')
              return
            }
            try {
              await createSpecies.mutateAsync(parsed.data)
              setIsAdding(false)
            } catch (caught) {
              setError(errorMessage(caught, 'Could not save. Please try again.'))
            }
          }}
        />
      ) : null}

      {rows.length === 0 ? (
        <p className="rounded-md border border-default px-3 py-6 text-center text-sm text-muted-text">
          No species yet. A survey can still record trees — they will read as
          &ldquo;not identified&rdquo; until there is something to choose.
        </p>
      ) : (
      <ul className="divide-y divide-default rounded-md border border-default">
        {rows.map((entry) => (
          <li key={entry.id}>
            <div
              className={cn(
                'flex items-center gap-3 px-3 py-2',
                entry.archivedAt === null ? null : 'opacity-55',
              )}
            >
              <TreeGlyph
                species={{
                  name: entry.name,
                  shape: entry.shape,
                  color: entry.color,
                }}
                size={18}
              />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {entry.name}
                {entry.archivedAt === null ? null : (
                  <span className="ml-2 text-xs font-normal text-muted-text">
                    Archived
                  </span>
                )}
              </span>
              {entry.archivedAt === null ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setError(null)
                    setIsAdding(false)
                    setEditingId(editingId === entry.id ? null : entry.id)
                  }}
                >
                  Edit
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                disabled={archiveSpecies.isPending}
                onClick={() => {
                  void handleArchiveToggle(entry)
                }}
              >
                {entry.archivedAt === null ? 'Archive' : 'Restore'}
              </Button>
            </div>

            {editingId === entry.id ? (
              <div className="border-t border-default bg-subtle px-3 py-3">
                <SpeciesForm
                  initial={{
                    name: entry.name,
                    shape: entry.shape,
                    color: entry.color,
                  }}
                  isSaving={updateSpecies.isPending}
                  submitLabel="Save changes"
                  onCancel={() => setEditingId(null)}
                  onSubmit={async (draft) => {
                    setError(null)
                    try {
                      await updateSpecies.mutateAsync({
                        id: entry.id,
                        input: draft,
                      })
                      setEditingId(null)
                    } catch (caught) {
                      setError(errorMessage(caught, 'Could not save. Please try again.'))
                    }
                  }}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      )}
    </section>
  )
}

interface SpeciesDraft {
  name: string
  shape: TreeMarkShape
  color: TreeMarkColor
}

function SpeciesForm({
  initial,
  isSaving,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: SpeciesDraft
  isSaving: boolean
  submitLabel: string
  onCancel: () => void
  onSubmit: (draft: SpeciesDraft) => Promise<void>
}) {
  const [draft, setDraft] = React.useState<SpeciesDraft>(initial)

  return (
    <form
      className="space-y-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        void onSubmit(draft)
      }}
    >
      <div className="max-w-xs space-y-1.5">
        <Label htmlFor="species-name">Name</Label>
        <Input
          id="species-name"
          value={draft.name}
          placeholder="Post oak"
          onChange={(event) => {
            setDraft((current) => ({ ...current, name: event.target.value }))
          }}
        />
      </div>

      <MarkPicker
        shape={draft.shape}
        color={draft.color}
        onChange={(mark) => {
          setDraft((current) => ({ ...current, ...mark }))
        }}
      />

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={isSaving}>
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {submitLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
